import React from 'react';
import {
  addDays,
  format,
  getDay,
  startOfWeek,
  endOfWeek,
  subDays,
  getYear,
  getWeek,
  getDate,
  startOfYear,
  endOfYear,
  differenceInCalendarWeeks,
  Day,
  getWeekYear,
} from 'date-fns';
import { formatToTimeZone } from 'date-fns-timezone';
import type { CalendarTileProperties } from 'react-calendar';
import { CurDay } from './Calendar.elements';
import {
  CalendarProps,
  FormatScheduleWeekProps,
  TimePickerValue,
} from './types';
import { Row, Text } from '@keyslabor/shared/ui';

interface CalendarOffsets {
  weeksOffset: number;
  firstDayOffset: number;
  timeZone: string;
  weekStartOptions: {
    weekStartsOn: Day;
    firstWeekContainsDate: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  };
}

export class CalendarUtils {
  private calendarOffsets: CalendarOffsets;

  constructor(calendarOffsets: CalendarOffsets) {
    this.calendarOffsets = calendarOffsets;
  }

  getCurYear(curViewDate: Date) {
    let curYear = getWeekYear(
      curViewDate,
      this.calendarOffsets.weekStartOptions
    );
    return curYear;

    if (
      getWeek(curViewDate) === 1 &&
      getDate(curViewDate) < this.calendarOffsets.firstDayOffset
    ) {
      curYear = curYear - 1;
    }

    const maxWeeks = this.getNumberOfWeeksInYear(curYear);
    const curWeek =
      getWeek(curViewDate, this.calendarOffsets.weekStartOptions) +
      this.calendarOffsets.weeksOffset;

    if (curWeek > maxWeeks) {
      curYear = curYear + 1;
    }

    return curYear;
  }

  getNumberOfWeeksInYear(year: number) {
    const startDate = startOfYear(new Date(year, 0, 1));
    const endDate = endOfYear(new Date(year, 11, 31));
    return differenceInCalendarWeeks(endDate, startDate);
  }

  getCurWeekNumber(date: Date = new Date()) {
    const curWeek = getWeek(date, this.calendarOffsets.weekStartOptions);
    // console.log('getCurWeekNumber', { curWeek, date }, this.calendarOffsets);
    const curYear = this.getCurYear(date);
    const maxWeeks = this.getNumberOfWeeksInYear(curYear);

    if (curWeek > maxWeeks) {
      return curWeek - maxWeeks;
    }

    return curWeek || 1;
  }

  getFormattedDateNames(value: Date = new Date()) {
    const startWeekDate = startOfWeek(
      value,
      this.calendarOffsets.weekStartOptions
    );
    const curWeek = this.getCurWeekNumber(startWeekDate);
    const year = this.getCurYear(startWeekDate);
    const month = format(startWeekDate, 'MMM');
    const day = format(startWeekDate, 'iiii');
    const currentDay = format(startWeekDate, 'd');
    //console.log('getFormattedDateNames', { startWeekDate, value, year });

    return {
      curWeek,
      year,
      month,
      day,
      currentDay,
    };
  }

  getNiceDate(value?: Date) {
    const { currentDay, month, day } = this.getFormattedDateNames(value);

    return `${day}, ${month} ${currentDay}`;
  }

  isStartNextWorkWeek(date: Date = new Date()) {
    const offset = this.calendarOffsets.firstDayOffset;
    const day = getDay(date);

    if (offset !== undefined && (day >= offset || day === 0)) {
      return true;
    }

    return false;
  }

  titleFormater({ date, minWeek, maxWeek }) {
    const weekNum = this.getCurWeekNumber(date);
    const minWeekNum = this.getCurWeekNumber(minWeek);
    const maxWeekNum = this.getCurWeekNumber(maxWeek);

    if (maxWeek && weekNum > maxWeekNum - 1) return true;
    if (minWeek && weekNum < minWeekNum + 1) return true;

    return false;
  }

  titleContent({
    weekSelect,
    date,
  }: Pick<CalendarProps, 'weekSelect' | 'highlightWeekOffset'> &
    Pick<CalendarTileProperties, 'date'>) {
    const render = format(date, 'd');
    let notify = false;
    if (this.isStartNextWorkWeek(date)) {
      notify = true;
    }

    if (
      weekSelect &&
      removeTime(date).toISOString() === removeTime().toISOString()
    ) {
      return (
        <CurDay borderLeft={notify ? '1px solid black' : 'none'}>
          {render}
        </CurDay>
      );
    }

    return <>{render}</>;
  }

  getTimeWithDate(date?: Date, time?: TimePickerValue) {
    const timeZone = this.calendarOffsets.timeZone;
    const timeDate = `${getFormattedDate(date ?? new Date())} ${
      time ? formatTime(time) : format(new Date(), 'hh:mm a')
    }`;

    return formatToTimeZone(timeDate, 'MM/DD/YYYY hh:mm a', {
      timeZone,
    });
  }

  getDateWeek(date: Date) {
    const startOfWeekDate = startOfWeek(
      new Date(date),
      this.calendarOffsets.weekStartOptions
    );
    return format(startOfWeekDate, 'MM/dd/yyyy');
  }

  getDateEndOfTheWeek(date: Date) {
    const endOfWeekDate = endOfWeek(
      new Date(date),
      this.calendarOffsets.weekStartOptions
    );
    return format(endOfWeekDate, 'MM/dd/yyyy');
  }
}

export function removeTime(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export const formatCalendarHeaderDate = (value: Date) => {
  return `${format(value, 'EEEE')}, ${format(value, 'MMMM')}
  ${format(value, 'd')}, ${format(value, 'y')}`;
};

export const formatShortWeekday = (weekday: string[]) => (_, date: Date) => {
  const d = new Date(date);

  return weekday[d.getDay()][0];
};

export const showTwoDigits = (d: number): string =>
  d.toString().padStart(2, '0');

export const getFormattedDate = (date: Date = new Date()) => {
  return format(date, 'MM/dd/yyyy');
};

export const formatTime = (time: TimePickerValue) => {
  const { amPm, h, m } = time;

  return `${h}:${showTwoDigits(m)} ${amPm.toUpperCase()}`;
};

export const dateToTimePickerValue = (
  date: Date = new Date()
): TimePickerValue => {
  const hours = parseInt(format(date, 'hh'), 10);
  const minutes = parseInt(format(date, 'mm'), 10);
  const amPm: string = format(date, 'a') as string;
  return {
    h: hours,
    m: minutes,
    amPm: amPm.toLowerCase() as TimePickerValue['amPm'],
  };
};

export const nextWeek = (date: Date = new Date()) => {
  return addDays(date, 7);
};

export const prevWeek = (date: Date = new Date()) => {
  return subDays(date, 7);
};

export const formatScheduleTime = (time?: string | null) => {
  if (!time) return '';

  if (time.match(/\d{1,2}:\d{1,2} (?=AM|PM)/)) {
    const hr = time.split(':')[0];
    return hr.length === 1 ? '0' + time : time;
  }

  const date = new Date();
  const [hours, minutes, seconds] = time.split(':');
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  date.setSeconds(parseInt(seconds));

  return format(date, 'hh:mm a');
};

export const getTimePickerValue = (
  time?: string | null
): TimePickerValue | null => {
  if (!time) return null;

  const timeArr = time.split(':');
  const hours = parseInt(timeArr[0]);
  const min = parseInt(timeArr[1]);

  // note: in order to distinguish between 12 and 24hr clock
  const splitedAmPm = time.split(' ')[1];
  const defaultAmPm: 'am' | 'pm' | null =
    splitedAmPm === 'AM' || splitedAmPm === 'PM'
      ? (splitedAmPm?.toLocaleLowerCase() as 'am' | 'pm')
      : null;

  let amPmHours = hours;
  let amPm: TimePickerValue['amPm'] = 'am';

  if (hours > 12) {
    amPmHours = hours - 12;
    amPm = 'pm';
  }

  return {
    amPm: defaultAmPm ?? amPm,
    h: amPmHours,
    m: min,
  };
};

export const formatScheduleWeekDay = ({
  inputDate,
  isGetParams,
  fSize,
  fWeight,
  isMobile,
}: FormatScheduleWeekProps) => {
  const regExp = /(\w{3}) (\d\d)\/(\d\d)/;
  if (!inputDate) return '';

  const params = inputDate.match(regExp);

  if (!params?.length) {
    return '';
  }

  const dateParams = params?.slice(1);

  const [dayName, month, day] = dateParams;

  if (isGetParams) {
    return dateParams;
  }

  return (
    <Row flexCenter width="max-content" height="100%">
      <Text fontWeight={fWeight || '400'} fontSize={fSize || '16px'}>
        {dayName}
      </Text>

      {isMobile ? (
        <Text fontWeight={fWeight || '400'} fontSize={fSize || '16px'}>
          {month}.{day}.{format(new Date(), 'yyyy')}
        </Text>
      ) : (
        <Text fontWeight={fWeight || '400'} fontSize={fSize || '16px'}>
          {month}/{day}/{format(new Date(), 'yyyy')}
        </Text>
      )}
    </Row>
  );
};

export const getDateToDay = (inputDate?: string) => {
  const date = inputDate ? new Date(inputDate) : new Date();

  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);

  return date;
};
